// Cartão do jogador na mesa (canvas 4.11): tocar no rosto abre, a partida segue e ele fecha sozinho na sua vez ou
// numa decisão de truco ou mão de onze do seu trio. Bot e você mesmo não têm botão de amizade. Quem não é bot mostra
// o retrospecto das últimas 10.
import { useEffect, useState } from 'react';
import type { LugarPublico } from '@truco6/mesa/protocolo';
import { rotuloDaDivisao } from '@truco6/gamificacao';
import { lerPerfilPublico } from '../comum/amigos.ts';
import { abrirPerfil } from '../comum/perfilAberto.ts';
import { sessaoDasRelacoes } from '../comum/relacoes.ts';
import { BotaoAmizade } from '../comum/BotaoAmizade.tsx';
import { BotaoTrio } from '../comum/BotaoTrio.tsx';
import { BotaoPerfil } from '../comum/BotaoPerfil.tsx';
import { Retrospecto } from '../comum/Retrospecto.tsx';
import { Rosto } from '../comum/Rosto.tsx';
import { ehBot, ehVaga, rostoDoLugar } from './textos.ts';

// Na vaga aberta, o cartão é do bot que joga no lugar, com o atalho para chamar amigos para sentar (aoChamar).
export function CartaoJogador({ lugar, nome, nos, eu, aoFechar, aoChamar }: {
  lugar: LugarPublico | null; nome: string; nos: boolean; eu: boolean; aoFechar: () => void; aoChamar?: () => void;
}) {
  const pessoa = lugar?.tipo === 'pessoa' ? lugar : null;
  const bot = ehBot(lugar);
  // A divisão vem do perfil público (bot não tem perfil).
  const [divisao, setDivisao] = useState<string | null>(null);
  const u = pessoa && !bot ? pessoa.id : null;
  useEffect(() => {
    const s = sessaoDasRelacoes();
    if (!u || !s) return;
    let vivo = true;
    lerPerfilPublico(s, u).then((r) => { if (vivo && 'divisao' in r) setDivisao(rotuloDaDivisao(r.divisao)); }, () => {});
    return () => { vivo = false; };
  }, [u]);
  const cor = nos ? 'var(--nos-400)' : 'var(--eles-400)';
  return (
    <div className="sheet cartao-jogador" role="dialog" aria-label={eu ? 'Você' : nome}>
      <div className="cab">
        <Rosto {...rostoDoLugar(lugar)} cor={cor} tamanho={64} moldura={bot ? undefined : pessoa?.moldura} coroa={!bot && !!pessoa?.coroa} />
        <div className="quem">
          <b>{eu ? 'Você' : nome}</b>
          <small><span style={{ color: cor }}>{nos ? 'NÓS' : 'ELES'}</span>{bot ? ' · BOT' : ''}{ehVaga(lugar) ? ' · VAGA ABERTA' : ''}</small>
          {pessoa && !bot && (pessoa.nivel || divisao) ? <small>{[pessoa.nivel ? `Nível ${pessoa.nivel}` : null, divisao].filter(Boolean).join(' · ')}</small> : null}
        </div>
        <button className="fechar-folha" aria-label="Fechar" onClick={aoFechar}>×</button>
      </div>
      {pessoa && !bot && <Retrospecto usuario={pessoa.id} apelido={eu ? 'você' : pessoa.perfil.apelido} />}
      {pessoa && !bot && (
        <div className="acoes">
          {!eu && <BotaoAmizade usuario={pessoa.id} apelido={pessoa.perfil.apelido} />}
          {!eu && <BotaoTrio usuario={pessoa.id} apelido={pessoa.perfil.apelido} />}
          <button className="btn2 ver-perfil" onClick={() => abrirPerfil(pessoa.id)}>Ver perfil</button>
        </div>
      )}
      {ehVaga(lugar) && aoChamar && (
        <div className="acoes"><button className="btn2 chamar-vaga" onClick={() => { aoFechar(); aoChamar(); }}>Chamar amigos para a vaga</button></div>
      )}
      <p className="rodape">A partida segue. Fecha sozinho na sua vez.</p>
    </div>
  );
}

// Fim de partida: os outros 5 da mesa, cada um com o botão de amizade e o de chamar para o trio (bot sem botão).
export function PessoasDaMesa({ lugares, eu, nomeDe }: { lugares: (LugarPublico | null)[]; eu: number; nomeDe: (s: number) => string }) {
  const meuTrio = eu % 2;
  return (
    <section className="na-mesa" aria-label="Quem jogou">
      <h2 className="rotulo">NA MESA</h2>
      <ul className="lista-pessoas">
        {lugares.map((l, s) => {
          if (s === eu || !l) return null;
          const nos = s % 2 === meuTrio, bot = ehBot(l), pessoa = l.tipo === 'pessoa' ? l : null;
          const cor = nos ? 'var(--nos-400)' : 'var(--eles-400)';
          return (
            <li key={s}>
              {pessoa && !bot ? (
                <BotaoPerfil usuario={pessoa.id} apelido={nomeDe(s)}>
                  <Rosto perfil={l.perfil} usuario={pessoa.id} cor={cor} tamanho={40} />
                  <span className="nome"><b>{nomeDe(s)}</b><small style={{ color: cor }}>{nos ? 'NÓS' : 'ELES'}</small></span>
                </BotaoPerfil>
              ) : (
                <>
                  <Rosto perfil={l.perfil} usuario={pessoa?.id} bot={bot} cor={cor} tamanho={40} />
                  <span className="nome"><b>{nomeDe(s)}</b><small style={{ color: cor }}>{nos ? 'NÓS' : 'ELES'}{bot ? ' · BOT' : ''}</small></span>
                </>
              )}
              {pessoa && <BotaoAmizade usuario={pessoa.id} apelido={pessoa.perfil.apelido} compacto />}
              {pessoa && <BotaoTrio usuario={pessoa.id} apelido={pessoa.perfil.apelido} compacto />}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
